import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../Authentication/AuthContext";
import Login from "../../Authentication/Login/Login";
import {
  injectAnswersSaveScript,
  type AnswersSaveConfig,
} from "./AnswersSave";

import "./BookViewPage.css";

type BookLevelKey = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

type BookViewStatus =
  | "checking"
  | "loading"
  | "active"
  | "inactive"
  | "error";

type ApiJsonResponse = {
  html?: string;
  content?: string;
  data?: {
    html?: string;
    content?: string;
  };
  message?: string;
  error?: string;
};

type PreloadedBookCache = {
  saved_at: number;
  email: string;
  level: BookLevelKey;
  book_id: number;
  title: string;
  file_name: string;
  html: string;
};

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_BACKEND_API_URL ||
  "https://www.api.dearlanguage.com"
).replace(/\/+$/, "");

const PRELOADED_BOOK_CACHE_PREFIX = "dear-language-preloaded-book-v2";
const PRELOADED_BOOK_CACHE_MAX_AGE_MS = 1000 * 60 * 30;

const ALL_BOOK_LEVELS: BookLevelKey[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

const normalizeBookLevel = (value: string | null): BookLevelKey => {
  const cleanValue = String(value || "A1")
    .trim()
    .toUpperCase();

  return ALL_BOOK_LEVELS.includes(cleanValue as BookLevelKey)
    ? (cleanValue as BookLevelKey)
    : "A1";
};

const normalizeEmail = (email?: string | null): string => {
  return String(email || "")
    .trim()
    .toLowerCase();
};

const normalizeId = (value?: string | null): string => {
  return String(value || "").trim();
};

const getValidBookId = (value: string | null, fallbackFile: string): number => {
  const directValue = Number(value || 0);

  if (
    Number.isInteger(directValue) &&
    directValue >= 1 &&
    directValue <= 10
  ) {
    return directValue;
  }

  const fileMatch = fallbackFile.match(/_([0-9]+)\.html$/i);
  const fileBookId = Number(fileMatch?.[1] || 0);

  if (
    Number.isInteger(fileBookId) &&
    fileBookId >= 1 &&
    fileBookId <= 10
  ) {
    return fileBookId;
  }

  return 1;
};

const getJsonHtml = (value: ApiJsonResponse): string => {
  return (
    value.html ||
    value.content ||
    value.data?.html ||
    value.data?.content ||
    ""
  );
};

const getJsonMessage = (value: ApiJsonResponse): string => {
  return value.message || value.error || "";
};

const escapeHtml = (value: string): string => {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

const getPreloadedBookCacheKey = (
  email: string,
  level: BookLevelKey,
  bookId: number,
): string => {
  return `${PRELOADED_BOOK_CACHE_PREFIX}:${normalizeEmail(
    email,
  )}:${level}:${bookId}`;
};

const getLastPreloadedBookCacheKey = (
  level: BookLevelKey,
  bookId: number,
): string => {
  return `${PRELOADED_BOOK_CACHE_PREFIX}:last:${level}:${bookId}`;
};

const isPreloadedBookCacheFresh = (
  cachedValue: PreloadedBookCache,
): boolean => {
  if (!cachedValue?.html || !cachedValue?.saved_at) {
    return false;
  }

  return Date.now() - cachedValue.saved_at <= PRELOADED_BOOK_CACHE_MAX_AGE_MS;
};

const getCachedPreloadedBook = (
  email: string,
  level: BookLevelKey,
  bookId: number,
): PreloadedBookCache | null => {
  try {
    const cleanEmail = normalizeEmail(email);
    const cacheKeys = cleanEmail
      ? [
          getPreloadedBookCacheKey(cleanEmail, level, bookId),
          getLastPreloadedBookCacheKey(level, bookId),
        ]
      : [getLastPreloadedBookCacheKey(level, bookId)];

    for (const cacheKey of cacheKeys) {
      const cachedValue =
        window.sessionStorage.getItem(cacheKey) ||
        window.localStorage.getItem(cacheKey);

      if (!cachedValue) {
        continue;
      }

      const parsed = JSON.parse(cachedValue) as PreloadedBookCache;

      if (!isPreloadedBookCacheFresh(parsed)) {
        window.sessionStorage.removeItem(cacheKey);
        window.localStorage.removeItem(cacheKey);
        continue;
      }

      if (parsed.level === level && parsed.book_id === bookId && parsed.html) {
        return parsed;
      }
    }

    return null;
  } catch {
    return null;
  }
};

const savePreloadedBook = (
  email: string,
  level: BookLevelKey,
  bookId: number,
  title: string,
  fileName: string,
  html: string,
): void => {
  const cleanHtml = String(html || "").trim();

  if (!cleanHtml) {
    return;
  }

  try {
    const cleanEmail = normalizeEmail(email);

    const payload: PreloadedBookCache = {
      saved_at: Date.now(),
      email: cleanEmail,
      level,
      book_id: bookId,
      title,
      file_name: fileName,
      html: cleanHtml,
    };

    window.sessionStorage.setItem(
      getLastPreloadedBookCacheKey(level, bookId),
      JSON.stringify(payload),
    );

    if (cleanEmail) {
      window.sessionStorage.setItem(
        getPreloadedBookCacheKey(cleanEmail, level, bookId),
        JSON.stringify(payload),
      );
    }
  } catch {
    // Cache ist optional.
  }
};

const ensureIframeHtml = (
  html: string,
  options: {
    title: string;
    answersSaveConfig: AnswersSaveConfig;
  },
): string => {
  const cleanHtml = String(html || "").trim();

  if (!cleanHtml) {
    return "";
  }

  const safeTitle = escapeHtml(options.title);

  const sanitizedHtml = cleanHtml.replace(
    /<meta[^>]+name=["']viewport["'][^>]*>/gi,
    "",
  );

  const baseTag = `<base href="${API_BASE_URL}/" />`;

  const metaTag =
    '<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover" />';

  const fixStyle = `
<style id="dear-language-book-view-frame-fix">
  html,
  body {
    margin: 0;
    padding: 0;
    min-height: 100%;
    width: 100%;
    background: #ffffff;
    overflow-x: hidden;
    -webkit-text-size-adjust: 100% !important;
    text-size-adjust: 100% !important;
    touch-action: pan-y;
  }

  body {
    width: 100%;
  }

  img,
  video,
  iframe,
  table {
    max-width: 100%;
  }

  input,
  textarea,
  select,
  button,
  [contenteditable="true"],
  [contenteditable="plaintext-only"],
  [role="textbox"],
  au[contenteditable] {
    font-size: 16px !important;
    line-height: 1.25 !important;
    -webkit-text-size-adjust: 100% !important;
    text-size-adjust: 100% !important;
    touch-action: manipulation;
  }

  input:focus,
  textarea:focus,
  select:focus,
  [contenteditable="true"]:focus,
  [contenteditable="plaintext-only"]:focus,
  [role="textbox"]:focus,
  au[contenteditable]:focus {
    font-size: 16px !important;
    outline-offset: 2px;
  }
</style>`;

  if (/<html[\s>]/i.test(sanitizedHtml)) {
    if (/<head[\s>]/i.test(sanitizedHtml)) {
      const htmlWithFrameFix = sanitizedHtml.replace(
        /<head([\s>]*)/i,
        `<head$1${metaTag}${baseTag}${fixStyle}`,
      );

      return injectAnswersSaveScript(
        htmlWithFrameFix,
        options.answersSaveConfig,
      );
    }

    const htmlWithHead = sanitizedHtml.replace(
      /<html([\s>]*)/i,
      `<html$1<head>${metaTag}${baseTag}${fixStyle}<title>${safeTitle}</title></head>`,
    );

    return injectAnswersSaveScript(
      htmlWithHead,
      options.answersSaveConfig,
    );
  }

  const completeHtml = `<!doctype html>
<html lang="de">
<head>
${metaTag}
${baseTag}
<title>${safeTitle}</title>
${fixStyle}
</head>
<body>
${sanitizedHtml}
</body>
</html>`;

  return injectAnswersSaveScript(
    completeHtml,
    options.answersSaveConfig,
  );
};

const BookViewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const fallbackTimerRef = useRef<number | null>(null);
  const previousViewportContentRef = useRef<string | null>(null);

  const { isLoggedIn, authLoading, userEmail, userId } = useAuth();

  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [status, setStatus] = useState<BookViewStatus>("checking");
  const [bookHtml, setBookHtml] = useState<string>("");
  const [errorText, setErrorText] = useState<string>("");
  const [retryKey, setRetryKey] = useState<number>(0);
  const [isBookLoaded, setIsBookLoaded] = useState<boolean>(false);
  const [hasInstantCachedBook, setHasInstantCachedBook] =
    useState<boolean>(false);

  const params = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  const level = normalizeBookLevel(params.get("level"));
  const fallbackFile = params.get("file") || `${level}_1.html`;
  const bookId = getValidBookId(params.get("bookId"), fallbackFile);
  const bookTitle = params.get("book") || `Deutsch ${level}.${bookId}`;

  const cleanEmail = normalizeEmail(userEmail);
  const cleanUserId = normalizeId(userId);

  const bookIdentity = `${level}-${bookId}-${location.search}`;

  const answersSaveConfig = useMemo<AnswersSaveConfig>(() => {
    return {
      level,
      bookId,
      bookTitle,
      fileName: fallbackFile,
      email: cleanEmail,
      userId: cleanUserId,
    };
  }, [bookId, bookTitle, cleanEmail, cleanUserId, fallbackFile, level]);

  const bookUrl = useMemo(() => {
    const searchParams = new URLSearchParams();

    if (cleanEmail) {
      searchParams.set("email", cleanEmail);
    }

    if (cleanUserId) {
      searchParams.set("app_user_id", cleanUserId);
    }

    searchParams.set("force_refresh", "1");
    searchParams.set("book_view", "1");
    searchParams.set("retry", String(retryKey));
    searchParams.set("_", String(Date.now()));

    return `${API_BASE_URL}/api/protected-books/${encodeURIComponent(
      level,
    )}/${encodeURIComponent(String(bookId))}?${searchParams.toString()}`;
  }, [bookId, cleanEmail, cleanUserId, level, retryKey]);

  const iframeKey = useMemo(() => {
    return `${bookIdentity}-${retryKey}-${bookHtml.length}`;
  }, [bookHtml.length, bookIdentity, retryKey]);

  const forceDefaultViewport = (): void => {
    const viewportContent =
      "width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover";

    let viewportMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]',
    );

    if (!viewportMeta) {
      viewportMeta = document.createElement("meta");
      viewportMeta.name = "viewport";
      document.head.appendChild(viewportMeta);
    }

    viewportMeta.setAttribute("content", viewportContent);

    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: window.scrollY,
        left: 0,
        behavior: "auto",
      });
    });
  };

  useEffect(() => {
    const viewportMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]',
    );

    previousViewportContentRef.current =
      viewportMeta?.getAttribute("content") || null;

    forceDefaultViewport();

    document.documentElement.classList.add("book-view-screen-page");
    document.body.classList.add("book-view-screen-page");

    document.documentElement.classList.remove("books-screen-page");
    document.body.classList.remove("books-screen-page");

    return () => {
      const currentViewportMeta = document.querySelector<HTMLMetaElement>(
        'meta[name="viewport"]',
      );

      if (currentViewportMeta && previousViewportContentRef.current) {
        currentViewportMeta.setAttribute(
          "content",
          previousViewportContentRef.current,
        );
      }

      document.documentElement.classList.remove("book-view-screen-page");
      document.body.classList.remove("book-view-screen-page");
    };
  }, []);

  useEffect(() => {
    const existingPreconnect = document.querySelector(
      'link[data-dear-language-api-preconnect="true"]',
    );

    if (existingPreconnect) {
      return;
    }

    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = API_BASE_URL;
    preconnect.setAttribute("data-dear-language-api-preconnect", "true");

    document.head.appendChild(preconnect);
  }, []);

  useEffect(() => {
    const cachedBook = getCachedPreloadedBook(cleanEmail, level, bookId);

    setStatus("checking");
    setBookHtml("");
    setErrorText("");
    setIsBookLoaded(false);
    setHasInstantCachedBook(false);

    if (cachedBook?.html) {
      const preparedCachedHtml = ensureIframeHtml(cachedBook.html, {
        title: cachedBook.title || bookTitle,
        answersSaveConfig,
      });

      if (preparedCachedHtml) {
        setBookHtml(preparedCachedHtml);
        setStatus("active");
        setIsBookLoaded(true);
        setHasInstantCachedBook(true);
      }
    }
  }, [answersSaveConfig, bookId, bookIdentity, bookTitle, cleanEmail, level]);

  useEffect(() => {
    let isCancelled = false;

    const controller = new AbortController();

    const timeoutId = window.setTimeout(() => {
      controller.abort();
    }, 30000);

    const loadBook = async (): Promise<void> => {
      setErrorText("");

      const cachedBook = getCachedPreloadedBook(cleanEmail, level, bookId);

      if (cachedBook?.html && !bookHtml) {
        const preparedCachedHtml = ensureIframeHtml(cachedBook.html, {
          title: cachedBook.title || bookTitle,
          answersSaveConfig,
        });

        if (preparedCachedHtml) {
          setBookHtml(preparedCachedHtml);
          setStatus("active");
          setIsBookLoaded(true);
          setHasInstantCachedBook(true);
        }
      }

      if (authLoading) {
        if (!cachedBook?.html) {
          setStatus("checking");
        }

        return;
      }

      if (!isLoggedIn) {
        setStatus("inactive");
        setBookHtml("");
        setIsBookLoaded(false);
        setHasInstantCachedBook(false);
        return;
      }

      if (!cleanEmail) {
        setStatus("error");
        setBookHtml("");
        setIsBookLoaded(false);
        setHasInstantCachedBook(false);
        setErrorText(
          "Ihre E-Mail-Adresse konnte nicht gelesen werden. Bitte loggen Sie sich erneut ein.",
        );
        return;
      }

      if (!cachedBook?.html) {
        setStatus("loading");
        setIsBookLoaded(false);
        setHasInstantCachedBook(false);
      }

      try {
        const response = await fetch(bookUrl, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
          headers: {
            Accept: "text/html,application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });

        if (isCancelled) {
          return;
        }

        const contentType = response.headers.get("content-type") || "";

        if (response.status === 401 || response.status === 403) {
          if (!cachedBook?.html) {
            setStatus("inactive");
            setBookHtml("");
            setIsBookLoaded(false);
            setHasInstantCachedBook(false);
          }

          return;
        }

        if (!response.ok) {
          if (cachedBook?.html) {
            return;
          }

          let responseMessage = "";

          try {
            if (contentType.toLowerCase().includes("application/json")) {
              const errorJson = (await response.json()) as ApiJsonResponse;
              responseMessage = getJsonMessage(errorJson);
            } else {
              responseMessage = await response.text();
            }
          } catch {
            responseMessage = "";
          }

          setStatus("error");
          setBookHtml("");
          setIsBookLoaded(false);
          setHasInstantCachedBook(false);
          setErrorText(
            responseMessage ||
              `Das Buch konnte nicht geladen werden. Status: ${response.status}`,
          );
          return;
        }

        let html = "";

        if (contentType.toLowerCase().includes("application/json")) {
          const json = (await response.json()) as ApiJsonResponse;
          html = getJsonHtml(json);

          if (!html) {
            if (cachedBook?.html) {
              return;
            }

            const jsonMessage = getJsonMessage(json);

            setStatus("error");
            setBookHtml("");
            setIsBookLoaded(false);
            setHasInstantCachedBook(false);
            setErrorText(
              jsonMessage ||
                "Die API hat keine HTML-Daten für dieses Buch zurückgegeben.",
            );
            return;
          }
        } else {
          html = await response.text();
        }

        const preparedHtml = ensureIframeHtml(html, {
          title: bookTitle,
          answersSaveConfig,
        });

        if (!preparedHtml) {
          if (cachedBook?.html) {
            return;
          }

          setStatus("error");
          setBookHtml("");
          setIsBookLoaded(false);
          setHasInstantCachedBook(false);
          setErrorText("Das Buch ist leer oder konnte nicht gelesen werden.");
          return;
        }

        savePreloadedBook(
          cleanEmail,
          level,
          bookId,
          bookTitle,
          fallbackFile,
          html,
        );

        if (!isCancelled) {
          setBookHtml(preparedHtml);
          setStatus("active");
          setIsBookLoaded(true);
          setHasInstantCachedBook(true);
        }
      } catch (error) {
        if (isCancelled) {
          return;
        }

        if (cachedBook?.html) {
          return;
        }

        if (error instanceof DOMException && error.name === "AbortError") {
          setStatus("error");
          setBookHtml("");
          setIsBookLoaded(false);
          setHasInstantCachedBook(false);
          setErrorText(
            "Das Buch braucht zu lange zum Laden. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.",
          );
          return;
        }

        setStatus("error");
        setBookHtml("");
        setIsBookLoaded(false);
        setHasInstantCachedBook(false);
        setErrorText(
          error instanceof Error
            ? error.message
            : "Das Buch konnte nicht geladen werden.",
        );
      }
    };

    void loadBook();

    return () => {
      isCancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [
    answersSaveConfig,
    authLoading,
    bookId,
    bookTitle,
    bookUrl,
    cleanEmail,
    fallbackFile,
    isLoggedIn,
    level,
    bookHtml,
  ]);

  useEffect(() => {
    if (fallbackTimerRef.current) {
      window.clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }

    if (status !== "active" || !bookHtml) {
      return;
    }

    fallbackTimerRef.current = window.setTimeout(() => {
      setIsBookLoaded(true);
    }, hasInstantCachedBook ? 30 : 300);

    return () => {
      if (fallbackTimerRef.current) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [bookHtml, hasInstantCachedBook, status]);

  const scrollInsideBook = (hash: string): void => {
    const iframeDocument = iframeRef.current?.contentDocument;

    if (!iframeDocument || !hash) {
      return;
    }

    const cleanHash = hash.replace("#", "");
    const target =
      iframeDocument.getElementById(cleanHash) ||
      iframeDocument.querySelector(`[name="${cleanHash}"]`);

    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const setupIframeControls = (): void => {
    const iframe = iframeRef.current;
    const iframeWindow = iframe?.contentWindow;
    const iframeDocument = iframe?.contentDocument;

    if (!iframeWindow || !iframeDocument) {
      return;
    }

    const editableSelector =
      'input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"], [role="textbox"], au[contenteditable]';

    const resetZoomOnEdit = (): void => {
      forceDefaultViewport();

      iframeDocument.documentElement.scrollLeft = 0;
      iframeDocument.body.scrollLeft = 0;

      iframeWindow.requestAnimationFrame(() => {
        iframeWindow.scrollTo({
          top: iframeWindow.scrollY,
          left: 0,
          behavior: "auto",
        });
      });
    };

    iframeDocument.querySelectorAll<HTMLElement>(editableSelector).forEach(
      (editableElement) => {
        editableElement.style.setProperty("font-size", "16px", "important");
        editableElement.style.setProperty("line-height", "1.25", "important");
        editableElement.style.setProperty(
          "-webkit-text-size-adjust",
          "100%",
          "important",
        );
        editableElement.style.setProperty(
          "text-size-adjust",
          "100%",
          "important",
        );
      },
    );

    iframeDocument.addEventListener(
      "focusin",
      (event: FocusEvent) => {
        const target = event.target as HTMLElement | null;

        if (target?.closest(editableSelector)) {
          resetZoomOnEdit();
        }
      },
      true,
    );

    iframeDocument.addEventListener(
      "touchend",
      (event: TouchEvent) => {
        const target = event.target as HTMLElement | null;

        if (target?.closest(editableSelector)) {
          resetZoomOnEdit();
        }
      },
      true,
    );

    iframeDocument.onclick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (!target) {
        return;
      }

      if (target.closest(editableSelector)) {
        resetZoomOnEdit();
      }

      const link = target.closest("a") as HTMLAnchorElement | null;

      if (!link) {
        return;
      }

      const hrefValue = link.getAttribute("href");

      if (!hrefValue) {
        return;
      }

      if (hrefValue.startsWith("#")) {
        event.preventDefault();
        event.stopPropagation();
        scrollInsideBook(hrefValue);
        return;
      }

      try {
        const parsedUrl = new URL(hrefValue, iframeWindow.location.href);

        if (parsedUrl.hash) {
          event.preventDefault();
          event.stopPropagation();
          scrollInsideBook(parsedUrl.hash);
        }
      } catch {
        return;
      }
    };
  };

  const handleIframeLoad = (): void => {
    setIsBookLoaded(true);

    window.setTimeout(() => {
      setupIframeControls();
    }, 0);
  };

  const handleLoginClose = (): void => {
    setShowLoginModal(false);
  };

  const renderLoadingIndicator = (
    extraClassName: string = "",
  ): React.ReactNode => {
    return (
      <div
        className={`book-view-loading-overlay ${extraClassName}`}
        role="status"
        aria-live="polite"
      >
        <div className="book-view-loading-spinner" aria-hidden="true" />

        <div className="book-view-loading-text">
          <strong>Buch wird geladen</strong>
          <span>Bitte warten Sie, bis das Buch vollständig geladen ist.</span>
        </div>
      </div>
    );
  };

  const renderLockedCard = (): React.ReactNode => {
    const isChecking = status === "checking" || authLoading;
    const isLoading = status === "loading";
    const isError = status === "error";

    if (isChecking || isLoading) {
      return (
        <div className="book-frame-panel book-frame-panel-loading">
          {renderLoadingIndicator("book-view-loading-panel")}
        </div>
      );
    }

    let badgeText = "Buchzugang";
    let title = "Dieses Buch ist nicht freigeschaltet";
    let text =
      "Bitte kaufen Sie den passenden Kurs oder prüfen Sie Ihren Zugang erneut.";

    if (!isLoggedIn) {
      badgeText = "Login erforderlich";
      title = "Bitte einloggen";
      text =
        "Bitte loggen Sie sich ein. Danach prüfen wir automatisch, ob dieses Buch für Ihr Konto freigeschaltet ist.";
    }

    if (isError) {
      badgeText = "Fehler";
      title = "Buch konnte nicht geöffnet werden";
      text = errorText || "Der Bücherzugang konnte nicht geprüft werden.";
    }

    return (
      <div className="book-view-center">
        <div className="book-lock-card">
          <p className="book-lock-badge">{badgeText}</p>

          <h2>{title}</h2>

          <p>{text}</p>

          {!isLoggedIn && (
            <button
              type="button"
              className="book-lock-button"
              onClick={() => setShowLoginModal(true)}
            >
              Einloggen
            </button>
          )}

          {isLoggedIn && status === "inactive" && (
            <button
              type="button"
              className="book-lock-button"
              onClick={() => navigate("/courses")}
            >
              Kurs kaufen
            </button>
          )}

          {isError && (
            <button
              type="button"
              className="book-lock-button"
              onClick={() => setRetryKey((current) => current + 1)}
            >
              Zugang erneut prüfen
            </button>
          )}

          <button
            type="button"
            className="book-lock-button"
            onClick={() => navigate("/books")}
          >
            Zurück zu Büchern
          </button>
        </div>
      </div>
    );
  };

  const shouldShowBook =
    status === "active" &&
    Boolean(bookHtml) &&
    (isLoggedIn || authLoading || hasInstantCachedBook);

  return (
    <div className="book-view-page">
      <Login isOpen={showLoginModal} onClose={handleLoginClose} />

      <section className="book-view-layout">
        <div className="book-view-topbar">
          <div className="book-view-title-block">
            <p className="book-view-kicker">{level} Deutschbuch</p>
            <h1 className="book-view-title">{bookTitle}</h1>
          </div>

          <div className="book-view-actions">
            <button
              type="button"
              className="book-view-back"
              onClick={() => navigate("/books")}
            >
              Zurück zu Büchern
            </button>
          </div>
        </div>

        {shouldShowBook ? (
          <div className="book-frame-panel">
            <iframe
              key={iframeKey}
              ref={iframeRef}
              title={bookTitle}
              className="book-frame"
              srcDoc={bookHtml}
              loading="eager"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              referrerPolicy="no-referrer"
              onLoad={handleIframeLoad}
            />

            {!isBookLoaded &&
              renderLoadingIndicator("book-view-loading-panel")}
          </div>
        ) : (
          renderLockedCard()
        )}
      </section>
    </div>
  );
};

export default BookViewPage;